import {
  EVENT_DAILY_REWARD_MODAL_CLOSED,
  EVENT_DAILY_REWARD_MODAL_OPENED,
  EVENT_OPEN_DAILY_REWARD_MODAL,
} from "../constants";

/**
 * Singleton class to track daily reward modal open status.
 * Tracks state via events to avoid DOM queries and ensure React state synchronization.
 * Follows the same singleton pattern used throughout the codebase (Logger, DepositFunnelTracker, etc.)
 */
export class DailyRewardModalTracker {
  private static instance: DailyRewardModalTracker | null = null;
  private isModalOpen = false;
  private isModalPending = false;
  private handlePending: () => void;
  private handleOpened: () => void;
  private handleClosed: () => void;

  private constructor() {
    // Initialize event handlers
    this.handlePending = () => {
      this.isModalPending = true;
    };

    this.handleOpened = () => {
      this.isModalPending = false;
      this.isModalOpen = true;
    };

    this.handleClosed = () => {
      this.isModalPending = false;
      this.isModalOpen = false;
    };

    // Set up event listeners
    window.addEventListener(EVENT_OPEN_DAILY_REWARD_MODAL, this.handlePending);
    window.addEventListener(EVENT_DAILY_REWARD_MODAL_OPENED, this.handleOpened);
    window.addEventListener(EVENT_DAILY_REWARD_MODAL_CLOSED, this.handleClosed);
  }

  static getInstance(): DailyRewardModalTracker {
    if (!DailyRewardModalTracker.instance) {
      DailyRewardModalTracker.instance = new DailyRewardModalTracker();
    }
    return DailyRewardModalTracker.instance;
  }

  /**
   * Checks if the daily reward modal is currently open.
   * @returns boolean indicating if the daily reward modal is currently open
   */
  isOpen(): boolean {
    return this.isModalOpen;
  }

  /**
   * Checks if the daily reward modal is pending open or currently open.
   * Use this to guard against race conditions where the modal has been requested
   * but hasn't rendered yet.
   */
  isPendingOrOpen(): boolean {
    return this.isModalPending || this.isModalOpen;
  }

  /**
   * Cleanup method to remove event listeners (useful for testing or cleanup scenarios).
   * Note: In normal operation, listeners persist for the app lifetime.
   */
  cleanup(): void {
    window.removeEventListener(EVENT_OPEN_DAILY_REWARD_MODAL, this.handlePending);
    window.removeEventListener(EVENT_DAILY_REWARD_MODAL_OPENED, this.handleOpened);
    window.removeEventListener(EVENT_DAILY_REWARD_MODAL_CLOSED, this.handleClosed);
  }
}
